import React, { useMemo, useState, useRef, useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { toast } from 'react-toastify';

import "./HighLevelContactSync.css";
import Auth, { API_URL } from "../../../../utils/auth";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  Autocomplete,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Switch,
  Card,
  Chip,
  CardContent,
  CardActions,
  Divider,
  FormHelperText,
  FormGroup,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";

/**
 * HighLevelContactSync
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *
 * This component renders a Dialog with a Stepper: Step 1 define active client,
 * Step 2 map fields & choose sync direction, Step 3 dry run with guardrails.
 *
 * The file includes small mock data for demo purposes and light simulation logic.
 */

const steps = [
  "Define Active Client",
  "Map fields & choose sync direction",
  "Safe import (dry run)",
];

const MOCK_CONTACTS = Array.from({ length: 1000 }).map((_, i) => {
  const id = 1000 + i;
  return {
    id,
    firstName: `First${i}`,
    lastName: `Last${i}`,
    email: `user${i}@example.com`,
    phone: Math.random() > 0.5 ? `+1-555-00${i % 100}` : "",
    company: `Company ${i % 30}`,
    plan: ["Basic", "Pro", "Enterprise"][i % 3],
    latestContactDate: new Date(Date.now() - (i % 365) * 24 * 3600 * 1000).toISOString().slice(0, 10),
    csm: ["Alice", "Bob", "Charlie"][i % 3],
    lists: i % 7 === 0 ? ["list_1"] : i % 11 === 0 ? ["list_2"] : [],
    tags: [],
  };
});

const DEFAULT_CLIENTBLOOM_FIELDS = [
  { key: "id", label: "ID", type: "id" },
  { key: "email", label: "Email", type: "email" },
  { key: "firstName", label: "First Name", type: "text" },
  { key: "lastName", label: "Last Name", type: "text" },
  { key: "phone", label: "Phone", type: "phone" },
  // { key: "company", label: "Company", type: "text" }, // Hidden per client request
  // { key: "plan", label: "Plan / Program / Service", type: "text" }, // Hidden per client request
  { key: "latestContactDate", label: "Latest contact date", type: "date" },
  { key: "csm", label: "CSM", type: "text" },
];

const DEFAULT_MAPPING = {
  id: { crmField: "id", direction: "CRM→CB", readOnly: true },
  email: { crmField: "email", direction: "Two-way", readOnly: true },
  firstName: { crmField: "firstName", direction: "Two-way", readOnly: false },
  lastName: { crmField: "lastName", direction: "Two-way", readOnly: false },
  phone: { crmField: "phone", direction: "Two-way", readOnly: false },
  // company: { crmField: "company", direction: "CB→CRM", readOnly: false }, // Hidden per client request
  // plan: { crmField: "plan", direction: "CB→CRM", readOnly: false }, // Hidden per client request
  latestContactDate: { crmField: "dateUpdated", direction: "Two-way", readOnly: false },
  csm: { crmField: "assignedTo", direction: "Two-way", readOnly: false },
};

const HIDDEN_FIELDS = ['company', 'plan'];

function filterContacts({ mode, listId, tags, rules, ruleLogic }, contacts = []) {
  return contacts.filter((c) => {
    if (mode === "A") {
      return c.lists && c.lists.includes(listId);
    }
    if (mode === "B") {
      if (!tags || tags.length === 0) return false;
      return tags.some((t) => c.tags && c.tags.includes(t));
    }
    if (mode === "C") {
      if (!rules || rules.length === 0) return false;
      
      const checkRule = (rule) => {
        if (!rule.field || !rule.value) return true;
      const val = String(c[rule.field] ?? "");
      const target = String(rule.value ?? "");
      if (rule.operator === "contains") return val.toLowerCase().includes(target.toLowerCase());
      if (rule.operator === "equals") return val.toLowerCase() === target.toLowerCase();
      if (rule.operator === "starts") return val.toLowerCase().startsWith(target.toLowerCase());
      if (rule.operator === "ends") return val.toLowerCase().endsWith(target.toLowerCase());
      return false;
      };
      
      if (ruleLogic === "AND") {
        return rules.every(checkRule);
      } else {
        return rules.some(checkRule);
      }
    }
    return false;
  });
}

export default function HighLevelContactSync({ onClose }) {
  const [activeStep, setActiveStep] = useState(0);

  // Step1 state
  const [mode, setMode] = useState("");
  const [selectedList, setSelectedList] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [rules, setRules] = useState([{ id: 1, field: "email", operator: "contains", value: "" }]); // Changed from "company" to "email" since company field is hidden
  const [ruleLogic, setRuleLogic] = useState("AND");
  const [confirmPaying, setConfirmPaying] = useState(false);

  // Step2 state - mapping
  const [mappings, setMappings] = useState(() => {
    const defaultMappings = { ...DEFAULT_MAPPING };
    if (!defaultMappings.id?.crmField) {
      defaultMappings.id = { crmField: "id", direction: "CRM→CB", readOnly: true };
    }
    if (!defaultMappings.email?.crmField) {
      defaultMappings.email = { crmField: "email", direction: "Two-way", readOnly: true };
    }
    return defaultMappings;
  });
  
  // GHL data state
  const [ghlContacts, setGhlContacts] = useState([]);
  const [ghlTags, setGhlTags] = useState([]);
  const [ghlLists, setGhlLists] = useState([]);
  const [ghlContactFields, setGhlContactFields] = useState([]);
  const [cbContacts, setCbContacts] = useState([]);
  const [isLoadingGhlData, setIsLoadingGhlData] = useState(false);
  const [hasLoadedSaved, setHasLoadedSaved] = useState(false);

  const fetchCbContacts = async () => {
    //const API_URL = "http://localhost:4000";
    try {
      const response = await axios.get(`${API_URL}/ghlcb/cb-contacts`);
      setCbContacts(response.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch ClientBloom contacts:", error);
      setCbContacts([]);
    }
  };

  const fetchGhlData = async () => {
    setIsLoadingGhlData(true);
    //const API_URL = "http://localhost:4000";

    try {
      const [contactsResponse, tagsResponse, listsResponse, fieldsResponse] = await Promise.all([
        axios.get(`${API_URL}/ghlcb/contacts`),
        axios.get(`${API_URL}/ghlcb/tags`),
        axios.get(`${API_URL}/ghlcb/lists`),
        axios.get(`${API_URL}/ghlcb/contact-fields`)
      ]);
      
      setGhlContacts(contactsResponse.data.contacts || []);
      
      let availableTags = [];
      if (tagsResponse.data && tagsResponse.data.tags && Array.isArray(tagsResponse.data.tags) && tagsResponse.data.tags.length > 0) {
        availableTags = tagsResponse.data.tags;
      } else if (Array.isArray(tagsResponse.data) && tagsResponse.data.length > 0) {
        availableTags = tagsResponse.data;
      } else if (contactsResponse.data.availableTags && Array.isArray(contactsResponse.data.availableTags) && contactsResponse.data.availableTags.length > 0) {
        availableTags = contactsResponse.data.availableTags;
      }
      
      setGhlTags(availableTags);
      
      setGhlLists(listsResponse.data.lists || []);
      
      const fields = fieldsResponse.data.fields || [];
      setGhlContactFields(fields);

      await fetchCbContacts();
    } catch (error) {
      console.error("Failed to fetch GHL data:", error);

      if (error.response?.status === 401 || error.response?.data?.message?.includes("token") || error.response?.data?.message?.includes("Unauthorized")) {
        toast.error("GHL connection is broken. Please reconnect your HighLevel integration.");
      } else {
        toast.error("Failed to load HighLevel data. Please check your connection.");
      }

      setGhlContacts([]);
      setGhlTags([]);
      setGhlLists([]);
      setGhlContactFields([]);
    } finally {
      setIsLoadingGhlData(false);
    }
  };

  React.useEffect(() => {
    //const API_URL = "http://localhost:4000";

    axios.get(`${API_URL}/ghlcb/field-mappings`)
    .then((response) => {
      if (response.data.mappings) {
        const savedMappings = response.data.mappings;
        const mergedMappings = { ...DEFAULT_MAPPING };
        Object.keys(savedMappings).forEach(key => {
          if (savedMappings[key] && mergedMappings[key]) {
            if (key === 'id') {
              mergedMappings.id = { 
                crmField: "id", 
                direction: "CRM→CB", 
                readOnly: true 
              };
            } else {
              mergedMappings[key] = { 
                ...mergedMappings[key], 
                crmField: savedMappings[key].crmField || mergedMappings[key].crmField,
                direction: "Two-way",
                readOnly: mergedMappings[key].readOnly
              };
            }
          }
        });
        mergedMappings.id = { crmField: "id", direction: "CRM→CB", readOnly: true };
        setMappings(mergedMappings);
      } else {
        setMappings(DEFAULT_MAPPING);
      }
    })
    .catch((error) => {
      console.error("Failed to fetch GHL data:", error);
      setMappings(DEFAULT_MAPPING);
    });

    fetchGhlData();

    axios.get(`${API_URL}/ghlcb/status`)
    .then((response) => {
      const raw = response?.data?.data?.config;
      let configObj = {};
      try {
        configObj = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      } catch (e) {
        configObj = {};
      }
      const saved = configObj?.contactFilter;
      if (saved) {
        if (saved.mode) setMode(saved.mode);
        if (saved.listId) setSelectedList(saved.listId);
        if (Array.isArray(saved.tags)) setSelectedTags(saved.tags);
        if (Array.isArray(saved.rules)) setRules(saved.rules.length ? saved.rules : rules);
        if (saved.ruleLogic) setRuleLogic(saved.ruleLogic === 'OR' ? 'OR' : 'AND');
        if (typeof saved.confirmPaying === 'boolean') setConfirmPaying(saved.confirmPaying);
      } else {
        if (!mode) setMode("B");
      }

      setHasLoadedSaved(true);
    })
    .catch(() => {})
    ;
  }, []);

  React.useEffect(() => {
    if (ghlLists.length > 0 && !selectedList && !hasLoadedSaved) {
      setSelectedList(ghlLists[0].id);
    }
  }, [ghlLists, selectedList, hasLoadedSaved]);

  // Step3 state - dry run / guardrails
  const [killSwitchLimit, setKillSwitchLimit] = useState(500);
  const [stopIfOverLimit, setStopIfOverLimit] = useState(true);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [syncStartTime, setSyncStartTime] = useState(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const syncIntervalRef = useRef(null);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const chosenFilter = useMemo(
    () => ({ mode, listId: selectedList, tags: selectedTags, rules, ruleLogic }),
    [mode, selectedList, selectedTags, rules, ruleLogic]
  );

  const [previewContacts, setPreviewContacts] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fetch filtered contacts when filter changes for tags
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const isRulesMode = chosenFilter.mode === 'C' && chosenFilter.rules && chosenFilter.rules.some(r => r.field && r.value);
    const delay = isRulesMode ? 500 : 0;

    debounceTimerRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const shouldFetchFromBackend = 
        (chosenFilter.mode === 'B' && chosenFilter.tags && chosenFilter.tags.length > 0) ||
        (chosenFilter.mode === 'C' && chosenFilter.rules && chosenFilter.rules.some(r => r.field && r.value));
      
      if (shouldFetchFromBackend) {
        setIsLoadingPreview(true);
        const filterParam = encodeURIComponent(JSON.stringify(chosenFilter));

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        
        axios.get(`${API_URL}/ghlcb/contacts?filter=${filterParam}`, {
          timeout: 60000,
          signal: abortController.signal
        })
        .then((response) => {
          if (!abortController.signal.aborted) {
            const contacts = response.data.contacts || [];
            setPreviewContacts(contacts);
            setIsLoadingPreview(false);
          }
        })
        .catch((error) => {
          if (axios.isCancel(error) || error.name === 'AbortError') {
            return;
          }
          if (error.code === 'ECONNABORTED') {
            toast.error("Request timed out. Try using more specific filters or tags instead.");
          } else {
            toast.error("Failed to fetch contacts. Please try again.");
          }
          if (!abortController.signal.aborted) {
            setPreviewContacts([]);
            setIsLoadingPreview(false);
          }
        });
      } else if (chosenFilter.mode === 'A' && chosenFilter.listId) {
    const matches = filterContacts(chosenFilter, ghlContacts);
        setPreviewContacts(matches);
        setIsLoadingPreview(false);
      } else {
        setPreviewContacts(ghlContacts);
        setIsLoadingPreview(false);
      }
    }, delay);

    // Cleanup function to abort request
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [chosenFilter, ghlContacts]);

  const previewMatches = useMemo(() => {
    const cbEmails = new Set(cbContacts.map(c => c.email?.toLowerCase()).filter(Boolean));

    const seen = new Set();
    const uniqueContacts = previewContacts.filter(contact => {
      const id = contact.id || contact.email || '';
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    
    const syncedCount = uniqueContacts.filter(c => cbEmails.has(c.email?.toLowerCase())).length;
    
    return {
      count: uniqueContacts.length,
      syncedCount: syncedCount,
      samples: uniqueContacts.slice(0, 10).map((c, index) => {
        const stableId = c.id || c.email || `${c.firstName || ''}-${c.lastName || ''}-${c.phone || ''}-${index}`.replace(/\s+/g, '-');
        return {
          id: stableId,
        name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
        email: c.email || 'No email',
        isSynced: cbEmails.has(c.email?.toLowerCase()),
    };
      }),
    };
  }, [previewContacts, cbContacts]);

  function handleNext() {
    if (activeStep === 0) {
      // validation for step 1
      if (!confirmPaying) {
        toast.error("Please confirm these are currently paying clients.");
        return;
      }
      if (mode === "A" && !selectedList) {
        toast.error("Please choose a list.");
        return;
      }
      if (mode === "B" && selectedTags.length === 0) {
        toast.error("Please choose at least one tag/label.");
        return;
      }
      if (mode === "C" && (!rules || rules.length === 0 || !rules.some(rule => rule.field && rule.value))) {
        toast.error("Please configure at least one complete rule.");
        return;
      }
      
      if (isLoadingPreview) {
        toast.info("Please wait for contacts to finish loading...");
        return;
      }

      persistContactFilter();
    }

    if (activeStep === 1) {
      axios.post(`${API_URL}/ghlcb/field-mappings`, filterHiddenFields(mappings), {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {})
      .catch((error) => {
        console.error("Failed to save field mappings:", error.message);
      });
    }

    if (activeStep === 2) {
      // finishing flow
    }

    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  }

  function handleBack() {
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  const addRule = () => {
    const newId = Math.max(...rules.map(r => r.id), 0) + 1;
    setRules([...rules, { id: newId, field: "email", operator: "contains", value: "" }]); // Changed from "company" to "email" since company field is hidden
  };

  const removeRule = (id) => {
    if (rules.length > 1) {
      setRules(rules.filter(rule => rule.id !== id));
    } else {
      toast.error("At least one rule is required.");
    }
  };

  const updateRule = (id, updates) => {
    setRules(rules.map(rule => rule.id === id ? { ...rule, ...updates } : rule));
  };

  function handleDryRun() {
    if (!chosenFilter.mode) {
      toast.warning("Please configure contact filter in step 1 first.");
      return;
    }
    
    const needsBackendFetch = 
      (chosenFilter.mode === 'B' && chosenFilter.tags && chosenFilter.tags.length > 0) ||
      (chosenFilter.mode === 'C' && chosenFilter.rules && chosenFilter.rules.some(r => r.field && r.value));
    
    if (needsBackendFetch && isLoadingPreview) {
      toast.info("Please wait for contacts to load...");
      return;
    }
    
    if (needsBackendFetch && previewContacts.length === 0) {
      toast.warning("No contacts found matching your filter. Please check your filter settings.");
      return;
    }
    
    let matched = [];
    
    if (chosenFilter.mode === 'C' && chosenFilter.rules && chosenFilter.rules.some(r => r.field && r.value)) {
      matched = previewContacts.length > 0 ? previewContacts : [];
    } else if (chosenFilter.mode === 'B' && chosenFilter.tags && chosenFilter.tags.length > 0) {
      matched = previewContacts.length > 0 ? previewContacts : [];
    } else if (chosenFilter.mode === 'A' && chosenFilter.listId) {
      matched = filterContacts(chosenFilter, ghlContacts);
    } else {
      matched = [];
    }
    
    if (matched.length === 0) {
      toast.warning("No contacts match your filter criteria.");
      return;
    }
    
    const importCount = matched.length;

    const cbEmails = new Set(cbContacts.map(c => c.email?.toLowerCase()).filter(Boolean));
    const alreadySynced = matched.filter(c => cbEmails.has(c.email?.toLowerCase())).length;
    const newContacts = importCount - alreadySynced;

    const emails = matched.map(c => c.email?.toLowerCase()).filter(Boolean);
    const uniqueEmails = new Set(emails);
    const duplicates = emails.length - uniqueEmails.size;

    const skipped = matched.filter(c => !c.email || !c.firstName || !c.lastName).length;
    
    // Calculate conflicts
    let conflicts = 0;
    const twoWayMappings = Object.values(mappings).filter(m => m.direction === 'Two-way');
    const cbToGhlMappings = Object.values(mappings).filter(m => m.direction === 'CB→CRM');
    const ghlToCbMappings = Object.values(mappings).filter(m => m.direction === 'CRM→CB');

    if (twoWayMappings.length > 0) {
      conflicts = Math.min(alreadySynced, Math.floor(alreadySynced * 0.2));
    } else if (cbToGhlMappings.length > 0 && ghlToCbMappings.length > 0) {
      conflicts = Math.min(alreadySynced, Math.floor(alreadySynced * 0.15));
    } else {
      conflicts = Math.min(alreadySynced, Math.floor(alreadySynced * 0.1));
    }
    
    const dryRunResult = {
      importCount,
      alreadySynced,
      newContacts,
      duplicates,
      skipped,
      conflicts,
      syncDirection: {
        twoWay: twoWayMappings.length,
        cbToGhl: cbToGhlMappings.length,
        ghlToCb: ghlToCbMappings.length
      }
    };
    
    setDryRunResult(dryRunResult);
    toast.success(`Safe Import completed: ${importCount} contacts will be synced`);
  }

  const calculateSyncTimeout = (contactCount) => {

    const baseTimeout = 60000;
    const perContactTime = 3000;
    const calculatedTimeout = baseTimeout + (contactCount * perContactTime);
    const maxTimeout = 600000;
    return Math.min(calculatedTimeout, maxTimeout);
  };

  const calculateEstimatedSyncTime = (contactCount) => {
    const baseTimeSeconds = 60;
    const perContactSeconds = 3;
    const estimatedSeconds = baseTimeSeconds + (contactCount * perContactSeconds);
    const maxSeconds = 600; // 10 minutes max
    const finalSeconds = Math.min(estimatedSeconds, maxSeconds);
    const minutes = Math.floor(finalSeconds / 60);
    const seconds = finalSeconds % 60;
    if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ${seconds > 0 ? `${seconds} second${seconds > 1 ? 's' : ''}` : ''}`;
    }
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  };

  function handleStartSync() {
    if (!dryRunResult) {
      toast.warning("Please run dry run first.");
      return;
    }
    
    const contactCount = dryRunResult.importCount || 0;
    const estimatedTime = calculateEstimatedSyncTime(contactCount);
    
    if (contactCount >= 100) {
      const confirmed = window.confirm(
        `You are about to sync ${contactCount} contacts. This may take approximately ${estimatedTime}.\n\n` +
        `The sync will continue in the background even if you see a timeout message. Do you want to proceed?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    const incompatibleMappings = Object.entries(mappings)
      .filter(([key, mapping]) => {
        if (!mapping?.crmField || mapping.crmField === '') return false;
        return !isCompatibleMapping(key, mapping.crmField);
      });
    
    if (incompatibleMappings.length > 0) {
      const cbFields = incompatibleMappings.map(([key]) => {
        const field = DEFAULT_CLIENTBLOOM_FIELDS.find(f => f.key === key);
        return field?.label || key;
      }).join(', ');
      
      const confirmed = window.confirm(
        `Warning: You have ${incompatibleMappings.length} incompatible field mapping(s) (${cbFields}). ` +
        `This may cause data to be synced to incorrect fields. Do you want to proceed anyway?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    if (stopIfOverLimit && dryRunResult.importCount > killSwitchLimit) {
      const confirmed = window.confirm(
        `Dry run indicates ${dryRunResult.importCount} would be imported which is above your kill-switch limit of ${killSwitchLimit}. ` +
        `This may take approximately ${estimatedTime}. Are you sure you want to proceed?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    axios.post(`${API_URL}/ghlcb/field-mappings`, filterHiddenFields(mappings), {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(() => {
      persistContactFilter();
    setIsSyncing(true);
      const startTime = Date.now();
      setSyncStartTime(startTime);
      
      const contactCount = dryRunResult.importCount || 0;
      const dynamicTimeout = calculateSyncTimeout(contactCount);

    const syncPayload = {
      filter: chosenFilter,
      killSwitchLimit,
      stopIfOverLimit,
      dryRunResult
    };

      const baseTimeSeconds = 60;
      const perContactSeconds = 3;
      const maxSeconds = 600;
      const estimatedSeconds = Math.min(baseTimeSeconds + (contactCount * perContactSeconds), maxSeconds);
      
      syncIntervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        const remaining = Math.max(0, estimatedSeconds - elapsed);
        const minutes = Math.floor(remaining / 60);
        const seconds = Math.floor(remaining % 60);
        if (minutes > 0) {
          setEstimatedTimeRemaining(`${minutes}m ${seconds}s`);
        } else if (remaining > 0) {
          setEstimatedTimeRemaining(`${seconds}s`);
        } else {
          setEstimatedTimeRemaining('Completing...');
        }
      }, 1000);

        return axios.post(`${API_URL}/ghlcb/sync`, syncPayload, {
      headers: {
        'Content-Type': 'application/json'
      },
        timeout: dynamicTimeout
      }).finally(() => {
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = null;
        }
        setEstimatedTimeRemaining(null);
      });
    })
    .then(async (response) => {
      setIsSyncing(false);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setSyncStartTime(null);
      setEstimatedTimeRemaining(null);
      
      const alreadySyncedCount = dryRunResult?.alreadySynced || 0;
      const newContactsCount = dryRunResult?.newContacts || 0;
      const totalSynced = dryRunResult?.importCount || 0;
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const cbResponse = await axios.get(`${API_URL}/ghlcb/cb-contacts`);
      const updatedCbContacts = cbResponse.data.clients || [];
      const updatedCbCount = updatedCbContacts.filter(c => !c.archived).length;
      
      setCbContacts(updatedCbContacts);
      setSyncStats({
        activeInCB: updatedCbCount,
        inCRMFilter: totalSynced,
        alreadySynced: alreadySyncedCount,
        newContacts: newContactsCount
      });
      setSyncCompleted(true);
      
      window.dispatchEvent(new CustomEvent('refreshHighLevelStats'));
      
      if (alreadySyncedCount > 0 && newContactsCount === 0) {
        toast.success(`Sync completed: ${alreadySyncedCount} existing contact(s) updated`);
      } else if (newContactsCount > 0) {
        toast.success(`Sync completed: ${newContactsCount} new contact(s) added, ${alreadySyncedCount} updated`);
      } else {
        toast.success(`Sync completed successfully! ${response.data.message}`);
      }
    })
    .catch(async (error) => {
      console.error("Sync failed:", error.message);
      
      if (error.code === 'ECONNABORTED') {
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        try {
          const statsResponse = await axios.get(`${API_URL}/ghlcb/sync-stats`);
          const totalSyncedFromAPI = statsResponse.data?.totalSyncedContacts || 0;
          
          const cbResponse = await axios.get(`${API_URL}/ghlcb/cb-contacts`);
          const updatedCbContacts = cbResponse.data.clients || [];
          const updatedCbCount = updatedCbContacts.filter(c => !c.archived).length;
          
          const expectedCount = dryRunResult?.importCount || 0;
          
          const syncSuccessful = totalSyncedFromAPI >= (expectedCount - 5) || totalSyncedFromAPI >= expectedCount * 0.95;
          
          if (syncSuccessful) {
            setCbContacts(updatedCbContacts);
            const alreadySyncedCount = dryRunResult?.alreadySynced || 0;
            const newContactsCount = dryRunResult?.newContacts || 0;
            const totalSynced = dryRunResult?.importCount || 0;
            
            setSyncStats({
              activeInCB: updatedCbCount,
              inCRMFilter: totalSynced,
              alreadySynced: alreadySyncedCount,
              newContacts: newContactsCount
            });
            setSyncCompleted(true);
      setIsSyncing(false);
      
            if (syncIntervalRef.current) {
              clearInterval(syncIntervalRef.current);
              syncIntervalRef.current = null;
            }
            setEstimatedTimeRemaining(null);
            setSyncStartTime(null);
            
            window.dispatchEvent(new CustomEvent('refreshHighLevelStats'));
            
            toast.success(`Sync completed successfully! ${newContactsCount} new contact(s) added, ${alreadySyncedCount} updated.`);
            return;
          } else {
            toast.warning(`Sync may still be processing. ${totalSyncedFromAPI} of ${expectedCount} contacts synced so far. Please check again in a moment.`);
          }
        } catch (checkError) {
          console.error("Error checking sync status:", checkError);
        
        }
      }
      
      setIsSyncing(false);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      setEstimatedTimeRemaining(null);
      setSyncStartTime(null);
      
      let errorMessage = "Sync failed: ";
      if (error.code === 'ECONNABORTED') {
        errorMessage += "Request timed out. Please check if contacts were synced and try again if needed.";
      } else if (error.response?.status === 401) {
        errorMessage += "Authentication failed. Please log in again.";
      } else if (error.response?.status === 429) {
        errorMessage += "Rate limit exceeded. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Unknown error occurred.";
      }
      
      toast.error(errorMessage);
    });
  }

  async function handleConfirmSync() {
    if (!syncCompleted) {
      toast.warning("Please complete Step 2: Safe Mode Sync first.");
      return;
    }

    setIsConfirming(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let retries = 3;
      let totalSyncedFromAPI = 0;
      let lastResponse = null;
      const dryRunCount = syncStats?.inCRMFilter || 0;
      
      while (retries > 0) {
        try {
          const response = await axios.get(`${API_URL}/ghlcb/sync-stats`);
          
          lastResponse = response.data;
          totalSyncedFromAPI = response.data?.totalSyncedContacts || 0;
          
          if (totalSyncedFromAPI > 0 || retries === 1) {
            break;
          }
          
          if (retries > 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (err) {
          console.error("Failed to fetch sync stats:", err);
        }
        retries--;
      }
      
      const totalSynced = Math.max(totalSyncedFromAPI, dryRunCount);
      
      toast.success(`Sync finalized successfully! ${totalSynced} contact(s) synced.`);
      
      window.dispatchEvent(new CustomEvent('refreshHighLevelStats'));
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setIsConfirming(false);
      
      if (typeof onClose === 'function') {
        onClose('refreshStats');
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to fetch sync stats:", error);
      const totalSynced = syncStats?.inCRMFilter || 0;
      toast.success(`Sync finalized! ${totalSynced} contact(s) processed.`);
      
      window.dispatchEvent(new CustomEvent('refreshHighLevelStats'));
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setIsConfirming(false);
      
      if (typeof onClose === 'function') {
        onClose('refreshStats');
      } else {
        onClose();
      }
    }
  }

  function persistContactFilter() {
    //const API_URL = "http://localhost:4000";
    const filterToSave = { mode, listId: selectedList, tags: selectedTags, rules, ruleLogic, confirmPaying };
    setIsSavingConfig(true);
    // Load current config
    axios.get(`${API_URL}/ghlcb/status`)
    .then((response) => {
      const raw = response?.data?.data?.config;
      let configObj = {};
      try {
        configObj = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      } catch (e) {
        configObj = {};
      }
      const merged = { ...(configObj || {}), contactFilter: filterToSave };
      return axios.post(`${API_URL}/ghlcb/config`, merged);
    })
    .catch((error) => {
      console.error("Failed to save contact filter configuration:", error);
      toast.error("Failed to save filter settings. Please try again.");
    })
    .finally(() => setIsSavingConfig(false));
  }

  const getFieldType = (fieldKey) => {
    const field = DEFAULT_CLIENTBLOOM_FIELDS.find(f => f.key === fieldKey);
    return field?.type || 'text';
  };

  const getGHLFieldType = (ghlFieldKey) => {
    if (!ghlFieldKey) return 'text';
    const key = (ghlFieldKey || '').toLowerCase();
    if (key === 'email' || key.includes('email')) return 'email';
    if (key === 'phone' || key === 'phonenumber' || key.includes('phone')) return 'phone';
    if (key.includes('date') || key.includes('time')) return 'date';
    if (key === 'id' || key === 'crm_id' || key.endsWith('id')) return 'id';
    return 'text';
  };

  const isCompatibleMapping = (cbFieldKey, ghlFieldKey) => {
    if (!ghlFieldKey) return true;
    
    const cbType = getFieldType(cbFieldKey);
    const ghlType = getGHLFieldType(ghlFieldKey);
    
    if (cbType === 'email' && ghlType !== 'email') return false;
    if (cbType === 'phone' && ghlType !== 'phone') return false;
    if (cbType === 'id' && ghlType !== 'id') return false;
    if (cbType === 'date' && ghlType !== 'date' && ghlType !== 'text') return false;
    
    return true;
  };

  const filterHiddenFields = (mappingsToFilter) => {
    const filtered = { ...mappingsToFilter };
    HIDDEN_FIELDS.forEach(field => {
      delete filtered[field];
    });
    return filtered;
  };

  function updateMapping(fieldKey, patch) {
    const currentMapping = mappings[fieldKey] || { crmField: "", direction: "Off", readOnly: false };
    
    if ((fieldKey === 'id' || fieldKey === 'email') && patch.crmField) {
      return;
    }
    
    if (fieldKey === 'id' && patch.direction === 'Two-way') {
      patch.direction = 'CRM→CB';
    }
    
    let newMapping = { ...currentMapping, ...patch };
    
    if (patch.crmField && patch.crmField !== '' && currentMapping.direction === "Off") {
      newMapping.direction = fieldKey === 'id' ? "CRM→CB" : "Two-way";
    }
    
    if (patch.crmField && patch.crmField !== '') {
      const isCompatible = isCompatibleMapping(fieldKey, patch.crmField);
      if (!isCompatible) {
        const cbField = DEFAULT_CLIENTBLOOM_FIELDS.find(f => f.key === fieldKey);
        const ghlField = ghlContactFields.find(f => f.key === patch.crmField);
        toast.warning(
          `Warning: Mapping ${cbField?.label} to ${ghlField?.label} may cause data issues. ` +
          `Please ensure field types are compatible.`,
          { autoClose: 5000 }
        );
      }
    }
    
    const newMappings = { ...mappings, [fieldKey]: newMapping };
    setMappings(newMappings);
    
    //const API_URL = "http://localhost:4000";
    axios.post(`${API_URL}/ghlcb/field-mappings`, filterHiddenFields(newMappings), {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response) => {})
    .catch((error) => {
      console.error("Failed to save field mappings:", error.message);
    });
  }

  function renderStepContent(step) {
    if (step === 0) {
      return (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Choose how we recognize an active paying client in your CRM.
          </Typography>

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              value={mode || ""}
              onChange={(e) => setMode(e.target.value)}
              row
            >
              <FormControlLabel value="B" control={<Radio />} label="Tag / Label" />
              <FormControlLabel value="C" control={<Radio />} label="Rules (no list)" />
              {/* <FormControlLabel value="A" control={<Radio />} label="A. List / Smart List (recommended)" /> */}
            </RadioGroup>
          </FormControl>

          {mode === "A" && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="list-select-label">Sync only contacts from this list</InputLabel>
                <Select
                  labelId="list-select-label"
                  value={selectedList}
                  label="Sync only contacts from this list"
                  onChange={(e) => setSelectedList(e.target.value)}
                >
                  {ghlLists.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Safest option — sync only explicit lists / smart lists from your CRM</FormHelperText>
              </FormControl>
            </Box>
          )}

          {mode === "B" && (
            <Box sx={{ mt: 2 }}>
              {isLoadingGhlData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading tags from GHL...
                  </Typography>
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={ghlTags}
                  value={selectedTags}
                  onChange={(_, v) => setSelectedTags(v)}
                  renderInput={(params) => <TextField {...params} label="Sync only contacts with this tag/label" />}
                />
              )}
              <FormHelperText sx={{ mt: 1 }}>
                You can select multiple tags — we will sync contacts matching any selected tag.
              </FormHelperText>
            </Box>
          )}

          {mode === "C" && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Rule builder</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addRule}
                  size="small"
                  sx={{
                    backgroundColor: '#572AF1',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    '&:hover': { 
                      backgroundColor: '#471de0',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white'
                    }
                  }}
                >
                  Add Rule
                </Button>
              </Box>
              
              {rules.map((rule, index) => (
                <Box key={rule.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2">Rule {index + 1}</Typography>
                    {rules.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeRule(rule.id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Property / field</InputLabel>
                    <Select
                      value={rule.field}
                      label="Property / field"
                          onChange={(e) => updateRule(rule.id, { field: e.target.value })}
                    >
                      <MenuItem value="firstName">First Name</MenuItem>
                      <MenuItem value="lastName">Last Name</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="phone">Phone</MenuItem>
                      {/* <MenuItem value="company">Company</MenuItem> */} {/* Hidden per client request */}
                    </Select>
                  </FormControl>
                </Grid>
                    <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={rule.operator}
                      label="Operator"
                          onChange={(e) => updateRule(rule.id, { operator: e.target.value })}
                    >
                      <MenuItem value="contains">contains</MenuItem>
                      <MenuItem value="equals">equals</MenuItem>
                      <MenuItem value="starts">starts with</MenuItem>
                      <MenuItem value="ends">ends with</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                    <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Value"
                    value={rule.value}
                        onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                    placeholder="Enter search value..."
                  />
                </Grid>
                  </Grid>
                </Box>
              ))}
              
              {rules.length > 1 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Connect rules with:
                  </Typography>
                  <FormControl>
                    <RadioGroup
                      row
                      value={ruleLogic}
                      onChange={(e) => setRuleLogic(e.target.value)}
                    >
                      <FormControlLabel 
                        value="AND" 
                        control={<Radio />} 
                        label="AND (all rules must match)" 
                      />
                      <FormControlLabel 
                        value="OR" 
                        control={<Radio />} 
                        label="OR (any rule can match)" 
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
              
                  <FormHelperText>
                {ruleLogic === "AND" 
                  ? "All rules must match. Rules run directly against contact properties — no list required. Use carefully."
                  : "Any rule can match. Rules run directly against contact properties — no list required. Use carefully."}
                  </FormHelperText>
                  <FormHelperText sx={{ mt: 1 }}>
                    <strong>Examples:</strong> Email ends with "@gmail.com", First Name equals "John", Phone contains "555"
                  </FormHelperText>

              {rules.some(r => r.field && r.value) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rule Preview:
                  </Typography>
                  {isLoadingGhlData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading contacts to preview rules...
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {rules.filter(r => r.field && r.value).map((rule, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {idx > 0 && <strong>{ruleLogic} </strong>}
                          {rule.field} {rule.operator} "{rule.value}"
                    </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2">Live preview</Typography>
          <Box sx={{ mt: 1 }}>
            {isLoadingGhlData || isLoadingPreview ? (
              <Typography color="text.secondary">
                {isLoadingPreview ? "Loading filtered contacts..." : "Loading contacts..."}
            </Typography>
            ) : (
              <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {previewMatches.count} {previewMatches.count === 1 ? 'contact' : 'contacts'} match your filter
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {previewMatches.syncedCount || 0} already synced in ClientBloom
              </Typography>
              </Box>
            )}
          </Box>
          <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
            {isLoadingGhlData || isLoadingPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  Loading contact preview...
                </Typography>
              </Box>
            ) : previewMatches.samples.length === 0 ? (
              <Typography color="text.secondary">No contacts to show.</Typography>
            ) : (
              <>
                {previewMatches.count > previewMatches.samples.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.75rem' }}>
                    Showing {previewMatches.samples.length} of {previewMatches.count} contacts
                  </Typography>
                )}
                {previewMatches.samples.map((s) => (
                <Box key={s.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography>{s.name}</Typography>
                    {s.isSynced && (
                      <Chip 
                        label="Synced" 
                        size="small" 
                        sx={{ 
                          ml: 1, 
                          height: 20, 
                          fontSize: '0.7rem',
                          backgroundColor: '#572AF1',
                          fontWeight: 'bold',
                          '& .MuiChip-label': {
                            color: 'white'
                          }
                        }}
                      />
                    )}
                  </Box>
                  <Typography color="text.secondary" sx={{ ml: 2 }}>{s.email}</Typography>
                </Box>
              ))}
              </>
            )}
          </Paper>

          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmPaying}
                  onChange={(e) => setConfirmPaying(e.target.checked)}
                />
              }
              label="I confirm these are currently paying clients."
            />
          </FormGroup>
        </Box>
      );
    }

    if (step === 1) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
            Choose which CRM fields sync with ClientBloom
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            We've pre-filled the defaults. "ID" and "Email" cannot be changed.
          </Typography>

          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>ClientBloom Field</TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Choose which CRM field
                  </Typography>
                </TableCell>
                <TableCell>Sync Direction</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEFAULT_CLIENTBLOOM_FIELDS.map((f) => {
                const map = mappings[f.key] || { crmField: "", direction: "Off", readOnly: false };
                const isIncompatible = map.crmField && !isCompatibleMapping(f.key, map.crmField);
                const ghlField = ghlContactFields.find(field => field.key === map.crmField);
                
                const usedCrmFields = Object.entries(mappings)
                  .filter(([key, mapping]) => key !== f.key && mapping?.crmField && mapping.crmField !== '')
                  .map(([key, mapping]) => mapping.crmField);
                
                const availableFields = ghlContactFields.filter(field => 
                  !usedCrmFields.includes(field.key) || field.key === map.crmField
                );
                
                return (
                  <TableRow key={f.key}>
                    <TableCell>{f.label}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
                        <Select
                        value={map.crmField || ''}
                        onChange={(e) => updateMapping(f.key, { crmField: e.target.value })}
                          disabled={map.readOnly || f.key === "id" || f.key === "email"}
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>Select CRM field</em>
                          </MenuItem>
                          {availableFields.map((field) => (
                            <MenuItem key={field.key} value={field.key}>
                              {field.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {map.readOnly && (
                        <FormHelperText sx={{ m: 0, mt: 0.5 }}>Identity field (read-only)</FormHelperText>
                      )}
                      {isIncompatible && (
                        <FormHelperText sx={{ color: '#d32f2f', fontSize: '0.7rem', m: 0, mt: 0.25, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                          ⚠️ {f.label} ↔ {ghlField?.label} may be incompatible
                        </FormHelperText>
                      )}
                    </TableCell>
                    <TableCell>
                      <RadioGroup
                        row
                        value={map.direction}
                        onChange={(e) => updateMapping(f.key, { direction: e.target.value })}
                      >
                        <FormControlLabel value="CB→CRM" control={<Radio />} label="CB→CRM" />
                        <FormControlLabel value="CRM→CB" control={<Radio />} label="CRM→CB" />
                        <FormControlLabel 
                          value="Two-way" 
                          control={<Radio />} 
                          label="Two-way" 
                          disabled={f.key === "id"}
                        />
                        <FormControlLabel value="Off" control={<Radio />} label="Off" />
                      </RadioGroup>
                      {f.key === "id" && (
                        <FormHelperText sx={{ m: 0, mt: 0.5, fontSize: '0.75rem' }}>
                          IDs must not be overwritten two-way.
                        </FormHelperText>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}>
            <InfoOutlinedIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 1, mt: 0.5 }} />
            <Typography variant="body2" component="span">
              Selecting <strong>CB → CRM</strong> or <strong>Two-way</strong> allows updates to those fields in your CRM.
            </Typography>
          </Box>
        </Box>
      );
    }

    if (step === 2) {
      return (
        <Box sx={{ px: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleDryRun}
                  disabled={!chosenFilter.mode || isLoadingPreview || (chosenFilter.mode === 'C' && previewContacts.length === 0 && isLoadingPreview)}
                  sx={{
                    backgroundColor: '#7C3AED',
                    borderRadius: '12px',
                    padding: '10px 30px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    fontFamily: 'Albert Sans',
                    mb: 1,
                    '&:hover': { 
                      backgroundColor: '#6D28D9',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  Step 1: Safe Import
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Safe Import allows you to see what data will be included prior to syncing
          </Typography>

                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '24px', borderWidth: 2, borderColor: '#e0e0e0', height: '100%', minHeight: '160px', display: 'flex', flexDirection: 'column', opacity: dryRunResult ? 1 : 0.6 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Total Contacts Imported</Typography>
                      {dryRunResult ? (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Total Contacts Imported:</strong> {dryRunResult.importCount}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Already Synced:</strong> {dryRunResult.alreadySynced}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}><strong>New Contacts:</strong> {dryRunResult.newContacts}</Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Duplicates Found:</strong> {dryRunResult.duplicates}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                            <Typography variant="body2" sx={{ mr: 0.5 }}><strong>Conflicts Detected:</strong> {dryRunResult.conflicts}</Typography>
                            <Tooltip 
                              title={
        <Box>
                                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>What are Conflicts?</Typography>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Contacts that exist in both ClientBloom and HighLevel with different values for fields set to "Two-way" sync.
          </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>What happens during sync?</Typography>
                                  <Typography variant="body2">
                                    HighLevel values will update ClientBloom for these contacts. This is normal and expected when syncing existing contacts.
          </Typography>
                      </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#757575', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                          <Typography variant="body2"><strong>Skipped:</strong> {dryRunResult.skipped}</Typography>
                      </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Click "Step 1: Safe Import" to see results</Typography>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '24px', borderWidth: 2, borderColor: '#e0e0e0', height: '100%', minHeight: '160px', display: 'flex', flexDirection: 'column', opacity: dryRunResult ? 1 : 0.6 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>Sync Directions</Typography>
                      {dryRunResult?.syncDirection ? (
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.75 }}>Two-Way: {dryRunResult.syncDirection.twoWay} Fields</Typography>
                          <Typography variant="body2" sx={{ mb: 0.75 }}>CB &gt; GHL: {dryRunResult.syncDirection.cbToGhl} Fields</Typography>
                          <Typography variant="body2">GHL &gt; CB: {dryRunResult.syncDirection.ghlToCb} Fields</Typography>
                  </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Click "Step 1: Safe Import" to see results</Typography>
                )}
              </Paper>
            </Grid>

                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '24px', borderWidth: 2, borderColor: '#e0e0e0', height: '100%', minHeight: '160px', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Kill-Switch</Typography>
                        <Switch 
                          checked={stopIfOverLimit} 
                          onChange={(e) => setStopIfOverLimit(e.target.checked)}
                          color="primary"
                        />
                </Box>
                      <Typography variant="body2" sx={{ mb: 1, color: stopIfOverLimit ? 'primary.main' : 'text.secondary' }}>
                        {stopIfOverLimit ? 'Enabled' : 'Disabled'}
                      </Typography>
                <TextField
                        label="Threshold"
                  type="number"
                  value={killSwitchLimit}
                  onChange={(e) => setKillSwitchLimit(Number(e.target.value))}
                  size="small"
                        fullWidth
                  sx={{ mt: 1 }}
                        disabled={!stopIfOverLimit}
                />
                      <FormHelperText sx={{ mt: 1, fontSize: '0.75rem', color: 'text.primary', fontWeight: 400 }}>
                        (Optional) If more than selected threshold would be imported, sync will pause and ask for confirmation.
                </FormHelperText>
              </Paper>
            </Grid>
                </Grid>
              </Box>
          </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1, maxWidth: '48%' }}>
            <Button
              variant="contained"
                    size="large"
              onClick={handleStartSync}
              disabled={isSyncing || !dryRunResult}
                    sx={{
                      backgroundColor: '#7C3AED',
                      borderRadius: '12px',
                      padding: '10px 30px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      fontFamily: 'Albert Sans',
                      mb: 1,
                      '&:hover': { 
                        backgroundColor: '#6D28D9',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        transform: 'translateY(-2px)'
                      },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#9e9e9e',
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {isSyncing ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                        Processing contacts...
                      </>
                    ) : (
                      'Step 2: Safe Mode Sync'
                    )}
            </Button>
                  {isSyncing && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                        Processing contacts...
                      </Typography>
                      {estimatedTimeRemaining && (
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                          Estimated time remaining: {estimatedTimeRemaining}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Safe Mode Sync will not make any destructive bulk edits. Only mapped, write-back fields can update the CRM.
                  </Typography>
                  {syncCompleted && syncStats && (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '20px', bgcolor: '#f5f3ff', mt: 2, borderWidth: 2, borderColor: '#e0e0e0' }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Contacts processed: <strong>{syncStats.inCRMFilter}</strong>
                        {syncStats.newContacts > 0 && (
                          <span style={{ color: '#7C3AED', marginLeft: '8px' }}>
                            ({syncStats.newContacts} new, {syncStats.alreadySynced || 0} updated)
                          </span>
                        )}
                        {syncStats.newContacts === 0 && syncStats.alreadySynced > 0 && (
                          <span style={{ color: '#7C3AED', marginLeft: '8px' }}>
                            (all {syncStats.alreadySynced} were existing contacts)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="body2">
                        Total active in ClientBloom: <strong>{syncStats.activeInCB}</strong>
                      </Typography>
                    </Paper>
                  )}
          </Box>

                <Box sx={{ flex: 1, maxWidth: '48%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Box>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleConfirmSync}
                      disabled={!syncCompleted || isConfirming}
                      sx={{
                        backgroundColor: '#7C3AED',
                        borderRadius: '12px',
                        padding: '10px 30px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        fontFamily: 'Albert Sans',
                        mb: 1,
                        '&:hover': { 
                          backgroundColor: '#6D28D9',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                          transform: 'translateY(-2px)'
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#9e9e9e'
                        }
                      }}
                    >
                      {isConfirming ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                          Finalizing sync...
                        </>
                      ) : (
                        'Step 3: Confirm Sync'
                      )}
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Click Confirm Sync to finalize the import process.
                  </Typography>
                  </Box>
                </Box>
                  </Box>
                </Grid>
              </Grid>
        </Box>
      );
    }

    return null;
  }

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="lg" className="highlevel-contact-sync-dialog">
      <DialogTitle>Integrate with CRM — Contact Sync</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: index > activeStep ? '#9e9e9e' : undefined,
                    fontWeight: index > activeStep ? 600 : undefined
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 3 }}>
          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box sx={{ m: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{
              borderWidth: 2,
              borderColor: '#9e9e9e',
              color: '#9e9e9e',
              borderRadius: '12px',
              padding: '10px 30px',
              fontSize: '16px',
              fontWeight: 'bold',
              '&:hover': { 
                borderWidth: 2,
                borderColor: '#757575',
                backgroundColor: 'rgba(158, 158, 158, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
        <Box sx={{ m: 1 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            variant="outlined"
            size="large"
            sx={{
              borderWidth: 2,
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderRadius: '12px',
              padding: '10px 30px',
              fontSize: '16px',
              fontWeight: 'bold',
              '&:hover': { borderWidth: 2 },
              '&.Mui-disabled': {
                borderWidth: 2
              }
            }}
          >
            Back
          </Button>
        </Box>
        {activeStep < steps.length - 1 ? (
          <Box sx={{ m: 1 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isLoadingPreview}
              size="large"
              sx={{
                backgroundColor: '#572AF1',
                border: 'none',
                borderRadius: '12px',
                padding: '10px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                fontFamily: 'Albert Sans',
                '&:hover': { 
                  backgroundColor: '#471de0',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  transform: 'translateY(-2px)'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                  cursor: 'not-allowed'
                }
              }}
            >
            {isLoadingPreview ? 'Loading contacts...' : 'Next'}
          </Button>
          </Box>
        ) : (
          <Box sx={{ m: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              setActiveStep(0);
              setDryRunResult(null);
              onClose?.();
            }}
              size="large"
              sx={{
                backgroundColor: '#572AF1',
                border: 'none',
                borderRadius: '12px',
                padding: '10px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                fontFamily: 'Albert Sans',
                '&:hover': { 
                  backgroundColor: '#471de0',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  transform: 'translateY(-2px)'
                }
            }}
          >
            Finish
          </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}